import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getLocalStorageLanguage } from 'commons';
import { SystemLanguageType } from 'constant/language';
import { getBrandOptions, BrandOption } from 'constant/brand';
import {
  getOrderStatusOptions,
  OrderStatusOption,
} from 'constant/oms-order-status';
import { getOrderTypeOptions, OrderTypeOption } from 'constant/oms-order-type';
import { RootState } from 'store';
import { CascaderOption, SelectOption, I18nLabel } from 'types/base';
import { SysUser } from 'types/sys';

export type SelectOptionLabelList = (SelectOption & I18nLabel)[];

export interface GlobalInfoState {
  language: SystemLanguageType;
  collapsed: boolean;
  colorSelectOptions: (SelectOption &
    I18nLabel & {
      refProperty1?: string;
      refProperty2?: string;
    })[];
  stampSelectOptions: SelectOptionLabelList;
  rankSelectOptions: SelectOptionLabelList;
  materialCascaderOptionsMap: { [key: string]: CascaderOption[] };
  productCategoryCascaderOptions: CascaderOption[];
  staffSelectOptions: (SelectOption & {
    subShop?: number[];
    status: number;
  } & Pick<SysUser, 'shop'>)[];
  typeSelectOptions: SelectOption[];
  hueSelectOptions: SelectOption[];
  hardwareSelectOptions: SelectOptionLabelList;
  accessorySelectOptions: SelectOptionLabelList;
  collectionSelectOptionsMap: { [key: string]: SelectOptionLabelList };
  orderStatusOptions: OrderStatusOption[];
  orderTypeOptions: OrderTypeOption[];
  countryOptions: (SelectOption & {
    nameI18n: {
      en: string;
      ja: string;
      zh_CN: string;
    };
  })[];
  countryCodeOptions: SelectOption[];
  brandNameSelectOptions: BrandOption[];
}

const initialState: GlobalInfoState = {
  language: getLocalStorageLanguage(),
  collapsed: (() => {
    const cachedCollapsed = localStorage.getItem('menu-collapsed');
    if (cachedCollapsed !== null) {
      return JSON.parse(cachedCollapsed);
    }
    return window.innerWidth < 768 ? true : false; // MOBILE_BREAKPOINT
  })(),
  colorSelectOptions: [],
  stampSelectOptions: [],
  rankSelectOptions: [],
  materialCascaderOptionsMap: {},
  productCategoryCascaderOptions: [],
  staffSelectOptions: [],
  typeSelectOptions: [],
  hueSelectOptions: [],
  hardwareSelectOptions: [],
  accessorySelectOptions: [],
  collectionSelectOptionsMap: {},
  orderStatusOptions: getOrderStatusOptions(),
  orderTypeOptions: getOrderTypeOptions(),
  countryOptions: [],
  countryCodeOptions: [],
  brandNameSelectOptions: getBrandOptions(),
};

export const globalInfoSlice = createSlice({
  name: 'globalInfo',
  initialState,
  reducers: {
    setLanguage: (
      state,
      action: PayloadAction<GlobalInfoState['language']>,
    ) => {
      state.language = action.payload;
    },
    setCollapsed: (
      state,
      action: PayloadAction<GlobalInfoState['collapsed']>,
    ) => {
      state.collapsed = action.payload;
      localStorage.setItem('menu-collapsed', JSON.stringify(action.payload));
    },
    setMaterialCascaderOptionMap: (
      state,
      action: PayloadAction<GlobalInfoState['materialCascaderOptionsMap']>,
    ) => {
      state.materialCascaderOptionsMap = action.payload;
    },
    setColorSelectOptions: (
      state,
      action: PayloadAction<GlobalInfoState['colorSelectOptions']>,
    ) => {
      state.colorSelectOptions = action.payload;
    },
    setStampSelectOptions: (
      state,
      action: PayloadAction<GlobalInfoState['stampSelectOptions']>,
    ) => {
      state.stampSelectOptions = action.payload;
    },
    setRankSelectOptions: (
      state,
      action: PayloadAction<GlobalInfoState['rankSelectOptions']>,
    ) => {
      state.rankSelectOptions = action.payload;
    },
    setProductCategoryCascaderOptions: (
      state,
      action: PayloadAction<GlobalInfoState['productCategoryCascaderOptions']>,
    ) => {
      state.productCategoryCascaderOptions = action.payload;
    },
    setStaffSelectOptions: (
      state,
      action: PayloadAction<GlobalInfoState['staffSelectOptions']>,
    ) => {
      state.staffSelectOptions = action.payload;
    },
    setTypeSelectOptions: (
      state,
      action: PayloadAction<GlobalInfoState['typeSelectOptions']>,
    ) => {
      state.typeSelectOptions = action.payload;
    },
    setHueSelectOptions: (
      state,
      action: PayloadAction<GlobalInfoState['hueSelectOptions']>,
    ) => {
      state.hueSelectOptions = action.payload;
    },
    setHardwareSelectOptions: (
      state,
      action: PayloadAction<GlobalInfoState['hardwareSelectOptions']>,
    ) => {
      state.hardwareSelectOptions = action.payload;
    },
    setAccessorySelectOptions: (
      state,
      action: PayloadAction<GlobalInfoState['accessorySelectOptions']>,
    ) => {
      state.accessorySelectOptions = action.payload;
    },
    setCollectionSelectOptionsMap: (
      state,
      action: PayloadAction<GlobalInfoState['collectionSelectOptionsMap']>,
    ) => {
      state.collectionSelectOptionsMap = action.payload;
    },
    setOrderStatusOptions: (
      state,
      action: PayloadAction<GlobalInfoState['orderStatusOptions']>,
    ) => {
      state.orderStatusOptions = action.payload;
    },
    setOrderTypeOptions: (
      state,
      action: PayloadAction<GlobalInfoState['orderTypeOptions']>,
    ) => {
      state.orderTypeOptions = action.payload;
    },
    setCountryOptions: (
      state,
      action: PayloadAction<GlobalInfoState['countryOptions']>,
    ) => {
      state.countryOptions = action.payload;
    },
    setCountryCodeOptions: (
      state,
      action: PayloadAction<GlobalInfoState['countryCodeOptions']>,
    ) => {
      state.countryCodeOptions = action.payload;
    },
  },
});

export const {
  setLanguage,
  setCollapsed,
  setMaterialCascaderOptionMap,
  setColorSelectOptions,
  setStampSelectOptions,
  setRankSelectOptions,
  setProductCategoryCascaderOptions,
  setStaffSelectOptions,
  setTypeSelectOptions,
  setHueSelectOptions,
  setHardwareSelectOptions,
  setAccessorySelectOptions,
  setCollectionSelectOptionsMap,
  setOrderStatusOptions,
  setOrderTypeOptions,
  setCountryOptions,
  setCountryCodeOptions,
} = globalInfoSlice.actions;

export const selectGlobalInfo = (state: RootState) => state.globalInfo;

export default globalInfoSlice.reducer;
